/**
 * Share links for an issued certificate.
 *
 * Everything here builds a URL around the ONE public, already-existing surface
 * for a certificate: `/verify-certificate/<code>` on the member app. There is
 * no private link, no token, and nothing here reveals more than that page
 * already shows a visitor who types the code — a share link that carried more
 * than the verification page itself would be a leak dressed as a feature.
 *
 * Pure string builders, so they are unit-tested and usable from any SPA.
 */

/** The public verification page for a certificate. */
export function certificateVerifyUrl(origin: string, code: string): string {
  return `${origin.replace(/\/+$/, '')}/verify-certificate/${encodeURIComponent(code)}`;
}

/**
 * LinkedIn's "Add to profile" prefill for a certification entry.
 *
 * `issueYear`/`issueMonth` are separate integer params (LinkedIn has no date
 * param), and month is 1-12 — `getMonth()` is 0-based, hence the +1.
 */
export function linkedInAddToProfileUrl(args: {
  name: string;
  organizationName: string;
  issuedAt: Date | string;
  certUrl: string;
  certId: string;
}): string {
  const d = args.issuedAt instanceof Date ? args.issuedAt : new Date(args.issuedAt);
  const params = new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: args.name,
    organizationName: args.organizationName,
    issueYear: String(d.getUTCFullYear()),
    issueMonth: String(d.getUTCMonth() + 1),
    certUrl: args.certUrl,
    certId: args.certId,
  });
  return `https://www.linkedin.com/profile/add?${params.toString()}`;
}

/** LinkedIn's share-a-link composer. */
export function linkedInShareUrl(url: string): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

/** Facebook's share-a-link composer. */
export function facebookShareUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

/** The text body for `navigator.share` and the clipboard. */
export function certificateShareText(args: {
  courseTitle: string;
  organizationName: string;
}): string {
  return `I completed “${args.courseTitle}” at ${args.organizationName}.`;
}
